import m from 'mithril';
import { formatRupiah, showToast, getMithril, postMithril, putMithril, deleteMithril, getUserData } from '../../utils.js';
import { getTerm, nomenclatureStream } from '../../js/nomenclature.js';
import { showConfirmation } from '../../js/toaster.js';

// Pinjaman Tab Component
const formatNumberInt = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "";
    // Ensure we only deal with digits before any potential decimal comma or point
    const cleanString = amount.toString().split(',')[0].split('.')[0].replace(/[^\d]/g, "");
    const num = parseInt(cleanString, 10);
    if (isNaN(num)) return "";
    return new Intl.NumberFormat("id-ID", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(num);
};

const PinjamanTab = {
    oninit: function (vnode) {
        this.loanProducts = [];
        this.isLoading = false;
        this.isFormVisible = false;
        this.isEditFormVisible = false;
        this.editingProduct = null;
        this.subunits = [];
        this.subunitsLoading = true;

        // Subscribe to nomenclature changes
        this.nomenclatureUnsubscribe = nomenclatureStream.map(() => {
            m.redraw();
        });

        // Load initial data
        this.loadData();

        // Load subunits first, then they can be used by forms
        this.loadSubunits();
    },

    loadData: async function () {
        this.isLoading = true;
        try {
            const response = await getMithril('/api/loan-products', {
                component: this,
                showLoading: true
            });

            if (response.success) {
                this.loanProducts = response.data;
            } else {
                showToast('Gagal memuat data produk pinjaman', 'error');
            }
        } catch (error) {
            console.error('Error loading loan products:', error);
            showToast('Gagal memuat data produk pinjaman: ' + error.message, 'error');
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    loadSubunits: async function () {
        try {
            const userData = await getUserData();

            // This component is only accessible to managers, so they must be assigned to a subunit
            if (userData.subunit_id) {
                this.subunits = [{
                    id: userData.subunit_id,
                    nama: userData.subunit_name || `Subunit ${userData.subunit_id}`
                }];
            } else {
                // This shouldn't happen if access control is working properly
                this.subunits = [];
            }
        } catch (error) {
            console.error('Error loading user data in loadSubunits:', error);
            this.subunits = [];
        } finally {
            this.subunitsLoading = false;
        }
    },

    showForm: function () {
        this.isFormVisible = true;
    },

    hideForm: function () {
        this.isFormVisible = false;
    },

    showEditForm: function (product) {
        this.editingProduct = product;
        this.isEditFormVisible = true;
    },

    hideEditForm: function () {
        this.isEditFormVisible = false;
        this.editingProduct = null;
    },

    refreshData: function () {
        this.loadData();
    },

    onunload: function (vnode) {
        // Unsubscribe from nomenclature stream to prevent memory leaks
        if (this.nomenclatureUnsubscribe) {
            this.nomenclatureUnsubscribe();
        }
    },

    deleteProduct: async function (productId) {
        showConfirmation(
            'Apakah Anda yakin ingin menghapus produk pinjaman ini?',
            async () => {
                // Confirmed - proceed with deletion
                try {
                    const response = await deleteMithril(`/api/loan-products?id=${productId}`, {
                        component: this,
                        showLoading: true
                    });

                    if (response.success) {
                        showToast('Produk pinjaman berhasil dihapus', 'success');
                        this.refreshData();
                    } else {
                        showToast('Gagal menghapus produk pinjaman: ' + response.error, 'error');
                    }
                } catch (error) {
                    console.error('Error deleting loan product:', error);

                    // Check if it's a 403 Forbidden error (superuser trying to access)
                    if (error.code === 403) {
                        // Redirect to dashboard
                        m.route.set('/dashboard');
                        return;
                    }

                    showToast('Gagal menghapus produk pinjaman: ' + error.message, 'error');
                }
            },
            () => {
                // Cancelled - do nothing
            }
        );
    },

    view: function () {
        const headerFlexClass = 'flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6';
        const headerTitleClass = 'text-xl font-bold text-gray-800 dark:text-white flex items-center';
        const headerDescClass = 'text-sm text-gray-600 dark:text-gray-400 mt-1';
        const iconClass = 'ri-money-dollar-circle-line mr-2 text-orange-500';
        const addButtonClass = 'bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-200 shadow-sm';
        const statsGridClass = 'grid grid-cols-1 md:grid-cols-4 gap-4 mb-6';
        const statCardClass = 'bg-white dark:bg-gray-800 rounded-lg shadow p-4';
        const statLabelClass = 'text-sm font-medium text-gray-500 dark:text-gray-400';
        const statValueClass = 'text-2xl font-bold text-gray-900 dark:text-white mt-1';
        const loadingSpinnerClass = 'animate-spin rounded-full h-12 w-12 border-4 border-orange-200 border-t-orange-500 mb-4';
        const loadingTextClass = 'text-gray-600 dark:text-gray-400 text-sm';
        const tableContainerClass = 'bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700';
        const tableClass = 'min-w-full divide-y divide-gray-200 dark:divide-gray-700';
        const theadClass = 'bg-gray-50 dark:bg-gray-700';
        const thClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider';
        const tbodyClass = 'bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700';
        const tdClass = 'px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300';

        return m('div', [
            // Header with title and add button
            m('div', { class: headerFlexClass }, [
                m('div', [
                    m('h2', { class: headerTitleClass }, [
                        m('i', { class: iconClass }),
                        'Produk Pinjaman'
                    ]),
                    m('p', { class: headerDescClass }, 'Kelola produk pinjaman unit simpan pinjam')
                ]),
                m('div', { class: 'flex space-x-2' }, [
                    m('button', {
                        class: addButtonClass,
                        onclick: () => this.showForm()
                    }, [
                        m('i', { class: 'ri-add-line mr-2' }),
                        'Tambah Produk'
                    ])
                ])
            ]),

            // Stats cards
            this.loanProducts.length > 0 && m('div', { class: statsGridClass }, [
                m('div', { class: statCardClass + ' border-l-4 border-orange-500' }, [
                    m('div', { class: statLabelClass }, 'Total Produk'),
                    m('div', { class: statValueClass }, this.loanProducts.length)
                ]),
                m('div', { class: statCardClass + ' border-l-4 border-blue-500' }, [
                    m('div', { class: statLabelClass }, `Rata-rata ${getTerm('fields.interest', 'Bunga')}`),
                    m('div', { class: statValueClass },
                        this.loanProducts.length > 0 ?
                            (this.loanProducts.reduce((sum, p) => sum + parseFloat(p.bunga || 0), 0) / this.loanProducts.length).toFixed(2) + '%'
                            : '0%')
                ]),
                m('div', { class: statCardClass + ' border-l-4 border-green-500' }, [
                    m('div', { class: statLabelClass }, 'Min Tenor'),
                    m('div', { class: statValueClass },
                        this.loanProducts.length > 0 ?
                            Math.min(...this.loanProducts.map(p => parseInt(p.tenor || 0))) + ' bulan'
                            : '0 bulan')
                ]),
                m('div', { class: statCardClass + ' border-l-4 border-purple-500' }, [
                    m('div', { class: statLabelClass }, 'Max Tenor'),
                    m('div', { class: statValueClass },
                        this.loanProducts.length > 0 ?
                            Math.max(...this.loanProducts.map(p => parseInt(p.tenor || 0))) + ' bulan'
                            : '0 bulan')
                ])
            ]),

            // Loading indicator with proper spinner
            this.isLoading ?
                m('div', { class: 'flex flex-col items-center justify-center py-12' }, [
                    m('div', { class: loadingSpinnerClass }),
                    m('p', { class: loadingTextClass }, 'Sedang memuat daftar produk pinjaman...')
                ]) :
                // Products table with improved styling
                m('div', { class: tableContainerClass }, [
                    m('div', { class: 'overflow-x-auto' }, [
                        m('table', { class: tableClass }, [
                            m('thead', { class: theadClass }, [
                                m('tr', [
                                    m('th', { class: thClass }, 'Kode'),
                                    m('th', { class: thClass }, 'Nama Produk'),
                                    m('th', { class: thClass }, getTerm('tableHeaders.subunit')),
                                    m('th', { class: thClass }, 'Tenor'),
                                    m('th', { class: thClass }, getTerm('fields.interest', 'Bunga')),
                                    m('th', { class: thClass }, getTerm('fields.interestFrequency', 'Frekuensi Bunga')),
                                    m('th', { class: thClass }, 'Plafon'),
                                    m('th', { class: thClass + ' text-right' }, 'Aksi')
                                ])
                            ]),
                            m('tbody', { class: tbodyClass }, [
                                this.loanProducts.length === 0 ?
                                    m('tr', [
                                        m('td', { class: 'px-6 py-12 text-center text-gray-500 dark:text-gray-400', colspan: '8' }, [
                                            m('div', { class: 'flex flex-col items-center justify-center' }, [
                                                m('p', { class: 'font-medium text-lg mb-2' }, 'Belum ada Produk Pinjaman'),
                                                m('p', { class: 'text-sm mt-1' }, 'Silakan buat produk pinjaman pertama dengan menekan tombol "Tambah Produk" di atas'),
                                                m('div', { class: 'mt-4' }, [
                                                    m('button', {
                                                        class: 'px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium transition-colors',
                                                        onclick: () => this.showForm()
                                                    }, 'Buat Produk Pertama')
                                                ])
                                            ])
                                        ])
                                    ]) :
                                    this.loanProducts.map((product, index) =>
                                        m('tr', {
                                            class: `hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'
                                                }`
                                        }, [
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white' }, product.kode),
                                            m('td', { class: tdClass }, product.nama),
                                            m('td', { class: tdClass },
                                                (() => {
                                                    const foundSubunit = this.subunits.find(s => s.id == product.subunit_id);
                                                    return foundSubunit?.nama || '-';
                                                })()
                                            ),
                                            m('td', { class: tdClass },
                                                (() => {
                                                    const tenorUnit = product.tenor_unit || 'months';
                                                    const unitMap = { 'days': 'hari', 'weeks': 'minggu', 'months': 'bulan', 'years': 'tahun' };
                                                    return product.tenor + ' ' + (unitMap[tenorUnit] || tenorUnit);
                                                })()
                                            ),
                                            m('td', { class: tdClass }, product.bunga + '%'),
                                            m('td', { class: tdClass },
                                                (() => {
                                                    const interestFrequency = product.interest_frequency || 'annually';
                                                    const freqMap = {
                                                        'daily': 'Harian',
                                                        'weekly': 'Mingguan',
                                                        'monthly': 'Bulanan',
                                                        'quarterly': 'Triwulanan',
                                                        'annually': 'Tahunan',
                                                        'seasonal': 'Musiman'
                                                    };
                                                    return freqMap[interestFrequency] || interestFrequency;
                                                })()
                                            ),
                                            m('td', { class: tdClass },
                                                formatRupiah(product.bawah) + ' - ' + formatRupiah(product.atas)
                                            ),
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-right text-sm font-medium' }, [
                                                m('button', {
                                                    class: 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 p-1 rounded-full hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors duration-200 mr-2',
                                                    onclick: () => this.showEditForm(product),
                                                    title: 'Edit'
                                                }, [
                                                    m('i', { class: 'ri-edit-line' })
                                                ]),
                                                m('button', {
                                                    class: 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200',
                                                    onclick: () => this.deleteProduct(product.id),
                                                    title: 'Hapus'
                                                }, [
                                                    m('i', { class: 'ri-delete-bin-line' })
                                                ])
                                            ])
                                        ])
                                    )
                            ])
                        ])
                    ])
                ]),

            // Add Product Form Modal
            this.isFormVisible && m(PinjamanForm, {
                onHide: () => this.hideForm(),
                onRefresh: () => this.refreshData(),
                subunits: this.subunits
            }),

            // Edit Product Form Modal
            this.isEditFormVisible && m(EditPinjamanForm, {
                product: this.editingProduct,
                onHide: () => this.hideEditForm(),
                onRefresh: () => this.refreshData(),
                subunits: this.subunits
            })
        ]);
    }
};

// Pinjaman Form Component
const PinjamanForm = {
    oninit: function (vnode) {
        this.isSubmitting = false;
        this.currentUserSubunitId = null;
        this.currentSubunitName = '';

        // Load user data asynchronously and store it
        this.loadUserData();

        // Capture vnode attributes
        this.onHide = vnode.attrs.onHide;
        this.onRefresh = vnode.attrs.onRefresh;
        this.subunits = vnode.attrs.subunits || [];

        this.formData = {
            code: '',
            name: '',
            subunit_id: 3, // Will be set after user data is loaded
            tenor: '',
            tenor_unit: 'months',
            bunga: '',
            interest_frequency: 'annually',
            bawah: '',
            atas: '',
            admin: '',
            provisi: '',
            erc_rate: 0,
            penalty_rate: 0,
            penalty_grace_days: 0
        };
        this.errors = {};
    },

    loadUserData: async function () {
        try {
            const userData = await getUserData();
            this.currentUserSubunitId = userData.subunit_id || 3;
            this.currentSubunitName = userData.subunit_name || `Subunit ${userData.subunit_id}`;

            // Update form data with subunit ID
            this.formData.subunit_id = this.currentUserSubunitId;
            m.redraw();
        } catch (error) {
            console.error('Error loading user data:', error);
            this.currentUserSubunitId = 3;
            this.currentSubunitName = 'Subunit tidak ditentukan';
        }
    },

    getCurrentUserSubunitId: function () {
        return this.currentUserSubunitId || 3;
    },

    getCurrentSubunitName: function () {
        const subunitId = this.currentUserSubunitId;

        if (!subunitId) {
            return 'Subunit tidak ditentukan - Harap login dengan manajer yang benar';
        }

        // If we have subunit name from user data, use it
        if (this.currentSubunitName && this.currentSubunitName !== `Subunit ${subunitId}`) {
            return this.currentSubunitName;
        }

        // Otherwise use subunit ID with proper formatting
        return `Subunit ${subunitId}`;
    },

    handleInput: function (field, value) {
        this.formData[field] = value;
        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    validateForm: function () {
        this.errors = {};
        let isValid = true;

        if (!this.formData.code.trim()) {
            this.errors.code = "Kode produk harus diisi";
            isValid = false;
        }

        if (!this.formData.name.trim()) {
            this.errors.name = "Nama produk harus diisi";
            isValid = false;
        }

        // Validate that subunit_id is present
        const subunitId = this.getCurrentUserSubunitId();
        if (!subunitId) {
            this.errors.general = "Subunit tidak ditentukan. Harap login dengan manajer yang sesuai.";
            isValid = false;
        }

        if (!this.formData.tenor || this.formData.tenor <= 0) {
            this.errors.tenor = "Tenor harus diisi dengan angka positif";
            isValid = false;
        }

        if (!this.formData.bunga || this.formData.bunga < 0) {
            this.errors.bunga = `${getTerm('fields.interest', 'Bunga')} harus diisi dengan angka non-negatif`;
            isValid = false;
        }

        if (!this.formData.bawah || this.formData.bawah < 0) {
            this.errors.bawah = "Plafon bawah harus diisi dengan angka non-negatif";
            isValid = false;
        }

        if (!this.formData.atas || this.formData.atas <= 0) {
            this.errors.atas = "Plafon atas harus diisi dengan angka positif";
            isValid = false;
        }

        if (this.formData.bawah && this.formData.atas &&
            parseFloat(this.formData.bawah) >= parseFloat(this.formData.atas)) {
            this.errors.bawah = "Plafon bawah harus lebih kecil dari plafon atas";
            isValid = false;
        }

        if (!this.formData.admin || this.formData.admin < 0) {
            this.errors.admin = "Biaya admin harus diisi dengan angka non-negatif";
            isValid = false;
        }

        if (!this.formData.provisi || this.formData.provisi < 0) {
            this.errors.provisi = "Biaya provisi harus diisi dengan angka non-negatif";
            isValid = false;
        }

        return isValid;
    },

    submitForm: async function () {
        console.log('🔥 submitForm called, formData:', this.formData);

        // Show loading state immediately
        this.isSubmitting = true;
        m.redraw(); // Force UI update immediately

        const validationResult = this.validateForm();
        console.log('🔍 Validation result:', validationResult, 'Errors:', this.errors);

        if (validationResult) {
            console.log('✅ Validation passed, proceeding with submission');

            try {
                // Ensure subunit_id is included and set correctly
                const userData = await getUserData();
                const submitData = {
                    ...this.formData,
                    subunit_id: this.formData.subunit_id || userData.subunit_id || 3
                };

                console.log('🎯 Sending loan product data:', submitData);

                const response = await postMithril("/api/loan-products", submitData, {
                    component: this,
                    showLoading: true
                });

                console.log('📡 API response:', response);
                if (response.success) {
                    showToast("Produk pinjaman berhasil disimpan", "success");
                    this.onRefresh();
                    this.onHide();
                } else {
                    showToast("Gagal menyimpan produk pinjaman: " + (response.error || "Kesalahan tidak diketahui"), "error");
                }
            } catch (error) {
                console.error("❌ Error saving loan product:", error);

                // Check if it's a 403 Forbidden error (superuser trying to access)
                if (error.code === 403) {
                    // Redirect to dashboard
                    m.route.set('/dashboard');
                    return;
                }

                showToast("Gagal menyimpan produk pinjaman: " + error.message, "error");
            } finally {
                this.isSubmitting = false;
                m.redraw();
            }
        } else {
            console.log('❌ Validation failed, resetting loading state');

            // Reset loading state and show validation errors
            this.isSubmitting = false;
            m.redraw();

            // Show validation errors via toast notification
            const errorMessages = Object.values(this.errors);
            if (errorMessages.length > 0) {
                showToast(`Form tidak valid: ${errorMessages.join(', ')}`, 'error');
            }
        }
    },

    view: function (vnode) {
        const { onHide } = vnode.attrs;

        // Form input class constants
        const inputClassBase = "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white";
        const formInputClass = inputClassBase + " flex-1";
        const formInputClassNoFlex = inputClassBase;
        const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300";
        const errorClass = "mt-1 text-sm text-red-600 dark:text-red-400";
        const helperTextClass = "mt-1 text-sm text-gray-500 dark:text-gray-400";


        const subunits = vnode.attrs.subunits || [
            { id: 1, nama: 'Test Subunit 1' },
            { id: 2, nama: 'Test Subunit 2' }
        ];

        return m("div", { class: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" }, [
            m("div", {
                class: "bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto"
            }, [
                m("div", { class: "bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-lg" }, [
                    m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white" }, "Tambah Produk Pinjaman"),
                    m("p", { class: "mt-1 text-sm text-gray-600 dark:text-gray-400" }, "Masukkan informasi produk pinjaman baru")
                ]),

                m("div", { class: "px-6 py-4" }, [
                    m("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-6" }, [
                        // Left column
                        m("div", [
                            // Code
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "code"
                                }, "Kode Produk"),
                                m("input", {
                                    type: "text",
                                    id: "code",
                                    class: formInputClassNoFlex,
                                    placeholder: "Masukkan kode produk (mis: KPR01)",
                                    value: this.formData.code,
                                    oninput: e => this.handleInput('code', e.target.value)
                                }),
                                this.errors.code && m("p", { class: errorClass }, this.errors.code)
                            ]),

                            // Name
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "name"
                                }, "Nama Produk"),
                                m("input", {
                                    type: "text",
                                    id: "name",
                                    class: formInputClassNoFlex,
                                    placeholder: "Masukkan nama produk",
                                    value: this.formData.name,
                                    oninput: e => this.handleInput('name', e.target.value)
                                }),
                                this.errors.name && m("p", { class: errorClass }, this.errors.name)
                            ]),

                            // Subunit field - replaced with disabled input showing current user's subunit
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "subunit"
                                }, getTerm('tableHeaders.subunit')),
                                m("input", {
                                    type: "text",
                                    id: "subunit",
                                    class: formInputClassNoFlex,
                                    value: this.getCurrentSubunitName(),
                                    disabled: true
                                }),
                                m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Unit/koperasi ditentukan berdasarkan pengguna saat ini")
                            ]),

                            // Tenor
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "tenor"
                                }, "Tenor"),
                                m("div", { class: "flex gap-2" }, [
                                    m("input", {
                                        type: "number",
                                        id: "tenor",
                                        class: formInputClass,
                                        placeholder: "Jumlah",
                                        value: this.formData.tenor,
                                        oninput: e => this.handleInput('tenor', e.target.value)
                                    }),
                                    m("select", {
                                        class: formInputClass,
                                        value: this.formData.tenor_unit,
                                        oninput: e => this.handleInput('tenor_unit', e.target.value)
                                    }, [
                                        m("option", { value: "days" }, "Hari"),
                                        m("option", { value: "weeks" }, "Minggu"),
                                        m("option", { value: "months" }, "Bulan"),
                                        m("option", { value: "years" }, "Tahun")
                                    ])
                                ]),
                                this.errors.tenor && m("p", { class: "errorClass" }, this.errors.tenor)
                            ]),

                            // Bunga
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "bunga"
                                }, `${getTerm('fields.interest', 'Bunga')} (%)`),
                                m("div", { class: "flex gap-2" }, [
                                    m("input", {
                                        type: "number",
                                        id: "bunga",
                                        step: "0.01",
                                        class: formInputClass,
                                        placeholder: "Persentase",
                                        value: this.formData.bunga,
                                        oninput: e => this.handleInput('bunga', e.target.value)
                                    }),
                                    m("select", {
                                        class: formInputClass,
                                        value: this.formData.interest_frequency,
                                        oninput: e => this.handleInput('interest_frequency', e.target.value)
                                    }, [
                                        m("option", { value: "daily" }, "Harian"),
                                        m("option", { value: "weekly" }, "Mingguan"),
                                        m("option", { value: "monthly" }, "Bulanan"),
                                        m("option", { value: "quarterly" }, "Triwulanan"),
                                        m("option", { value: "annually" }, "Tahunan"),
                                        m("option", { value: "seasonal" }, "Musiman")
                                    ])
                                ]),
                                this.errors.bunga && m("p", { class: "errorClass" }, this.errors.bunga)
                            ]),
                        ]),

                        // Right column
                        m("div", [
                            // Bawah (Plafon bawah)
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "bawah"
                                }, "Plafon Bawah (Rp)"),
                                m("input", {
                                    type: "text",
                                    id: "bawah",
                                    class: formInputClassNoFlex,
                                    placeholder: "Masukkan plafon bawah",
                                    value: formatNumberInt(this.formData.bawah),
                                    oninput: e => {
                                        const rawValue = e.target.value.replace(/[^\d]/g, '');
                                        this.handleInput('bawah', rawValue);
                                    }
                                }),
                                this.errors.bawah && m("p", { class: "errorClass" }, this.errors.bawah)
                            ]),

                            // Atas (Plafon atas)
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "atas"
                                }, "Plafon Atas (Rp)"),
                                m("input", {
                                    type: "text",
                                    id: "atas",
                                    class: inputClassBase,
                                    placeholder: "Masukkan plafon atas",
                                    value: formatNumberInt(this.formData.atas),
                                    oninput: e => {
                                        const rawValue = e.target.value.replace(/[^\d]/g, '');
                                        this.handleInput('atas', rawValue);
                                    }
                                }),
                                this.errors.atas && m("p", { class: "errorClass" }, this.errors.atas)
                            ]),

                            // Admin
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "admin"
                                }, "Biaya Admin (Rp)"),
                                m("input", {
                                    type: "text",
                                    id: "admin",
                                    class: inputClassBase,
                                    placeholder: "Masukkan biaya admin",
                                    value: formatNumberInt(this.formData.admin),
                                    oninput: e => {
                                        const rawValue = e.target.value.replace(/[^\d]/g, '');
                                        this.handleInput('admin', rawValue);
                                    }
                                }),
                                this.errors.admin && m("p", { class: "errorClass" }, this.errors.admin)
                            ]),

                            // Provisi
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "provisi"
                                }, "Biaya Provisi (Rp)"),
                                m("input", {
                                    type: "text",
                                    id: "provisi",
                                    class: inputClassBase,
                                    placeholder: "Masukkan biaya provisi",
                                    value: formatNumberInt(this.formData.provisi),
                                    oninput: e => {
                                        const rawValue = e.target.value.replace(/[^\d]/g, '');
                                        this.handleInput('provisi', rawValue);
                                    }
                                }),
                                this.errors.provisi && m("p", { class: "errorClass" }, this.errors.provisi)
                            ]),

                            // ERC Rate
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "erc_rate"
                                }, "Biaya Pelunasan Dini (%)"),
                                m("input", {
                                    type: "number",
                                    id: "erc_rate",
                                    step: "0.01",
                                    min: "0",
                                    max: "100",
                                    class: inputClassBase,
                                    placeholder: "Masukkan biaya pelunasan dini dalam persen",
                                    value: this.formData.erc_rate || 0,
                                    oninput: e => this.handleInput('erc_rate', e.target.value)
                                }),
                                m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Persentase biaya tambahan jika pinjaman dilunasi sebelum waktunya")
                            ]),

                            // Penalty Section Header
                            m("div", { class: "mt-6 mb-2" }, [
                                m("h4", { class: "text-sm font-semibold text-gray-700 dark:text-gray-300" }, "Denda Keterlambatan")
                            ]),

                            // Penalty Rate and Grace Days in a row
                            m("div", { class: "flex gap-4" }, [
                                m("div", { class: "flex-1" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "penalty_rate"
                                    }, "Denda (% per Hari)"),
                                    m("input", {
                                        type: "number",
                                        id: "penalty_rate",
                                        step: "0.01",
                                        min: "0",
                                        max: "100",
                                        class: inputClassBase,
                                        placeholder: "0.00",
                                        value: this.formData.penalty_rate || 0,
                                        oninput: e => this.handleInput('penalty_rate', e.target.value)
                                    })
                                ]),
                                m("div", { class: "flex-1" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "penalty_grace_days"
                                    }, "Hari Tenggang"),
                                    m("div", { class: "flex items-center gap-2" }, [
                                        m("input", {
                                            type: "number",
                                            id: "penalty_grace_days",
                                            step: "1",
                                            min: "0",
                                            class: inputClassBase,
                                            placeholder: "0",
                                            value: this.formData.penalty_grace_days || 0,
                                            oninput: e => this.handleInput('penalty_grace_days', e.target.value)
                                        }),
                                        m("span", { class: "text-sm text-gray-500 dark:text-gray-400" }, "hari")
                                    ])
                                ])
                            ]),

                            // Helper text for penalty
                            m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Denda harian dari jumlah angsuran. Hari tenggang adalah masa sebelum denda dikenakan setelah jatuh tempo."),
                        ])
                    ])
                ]),

                m("div", { class: "bg-gray-50 dark:bg-gray-700 px-6 py-3 rounded-b-lg flex justify-end space-x-3" }, [
                    m("button", {
                        type: "button",
                        class: "inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500",
                        onclick: onHide,
                        disabled: this.isSubmitting
                    }, "Batal"),
                    m("button", {
                        type: "button",
                        class: "inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500",
                        onclick: () => this.submitForm(),
                        disabled: this.isSubmitting
                    }, [
                        this.isSubmitting ? [
                            m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                            "Menyimpan..."
                        ] : "Simpan"
                    ])
                ])
            ])
        ]);
    }
};

// Edit Pinjaman Form Component
const EditPinjamanForm = {
    oninit: function (vnode) {
        const product = vnode.attrs.product;
        this.isSubmitting = false;

        this.formData = {
            code: product.kode || '',  // Fixed: use 'kode' instead of 'code'
            name: product.nama || '',  // Fixed: use 'nama' instead of 'name'
            subunit_id: product.subunit_id,
            tenor: product.tenor || '',
            tenor_unit: product.tenor_unit || 'months',
            bunga: product.bunga || '',
            interest_frequency: product.interest_frequency || 'annually',
            bawah: product.bawah || '',
            atas: product.atas || '',
            admin: product.admin || '',
            provisi: product.provisi || '',
            erc_rate: product.erc_rate || 0,
            penalty_rate: product.penalty_rate || 0,
            penalty_grace_days: product.penalty_grace_days || 0
        };
        this.errors = {};
        this.productId = product.id;

        // Capture vnode attributes
        this.onHide = vnode.attrs.onHide;
        this.onRefresh = vnode.attrs.onRefresh;
        this.subunits = vnode.attrs.subunits || [];
    },

    handleInput: function (field, value) {
        this.formData[field] = value;
        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    validateForm: function () {
        this.errors = {};
        let isValid = true;

        if (!this.formData.code.trim()) {
            this.errors.code = "Kode produk harus diisi";
            isValid = false;
        }

        if (!this.formData.name.trim()) {
            this.errors.name = "Nama produk harus diisi";
            isValid = false;
        }

        if (!this.formData.tenor || this.formData.tenor <= 0) {
            this.errors.tenor = "Tenor harus diisi dengan angka positif";
            isValid = false;
        }

        if (!this.formData.bunga || this.formData.bunga < 0) {
            this.errors.bunga = `${getTerm('fields.interest', 'Bunga')} harus diisi dengan angka non-negatif`;
            isValid = false;
        }

        if (!this.formData.bawah || this.formData.bawah < 0) {
            this.errors.bawah = "Plafon bawah harus diisi dengan angka non-negatif";
            isValid = false;
        }

        if (!this.formData.atas || this.formData.atas <= 0) {
            this.errors.atas = "Plafon atas harus diisi dengan angka positif";
            isValid = false;
        }

        if (this.formData.bawah && this.formData.atas &&
            parseFloat(this.formData.bawah) >= parseFloat(this.formData.atas)) {
            this.errors.bawah = "Plafon bawah harus lebih kecil dari plafon atas";
            isValid = false;
        }

        if (!this.formData.admin || this.formData.admin < 0) {
            this.errors.admin = "Biaya admin harus diisi dengan angka non-negatif";
            isValid = false;
        }

        if (!this.formData.provisi || this.formData.provisi < 0) {
            this.errors.provisi = "Biaya provisi harus diisi dengan angka non-negatif";
            isValid = false;
        }

        return isValid;
    },

    submitForm: async function () {
        if (this.validateForm()) {
            this.isSubmitting = true;

            try {
                // Get user data to ensure we have the latest subunit information
                const userData = await getUserData();

                // Ensure subunit_id is included and map field names to match backend expectations
                const submitData = {
                    id: this.productId,
                    kode: this.formData.code,
                    nama: this.formData.name,
                    subunit_id: this.formData.subunit_id || userData.subunit_id,
                    tenor: this.formData.tenor,
                    tenor_unit: this.formData.tenor_unit,
                    bunga: this.formData.bunga,
                    interest_frequency: this.formData.interest_frequency,
                    bawah: this.formData.bawah,
                    atas: this.formData.atas,
                    admin: this.formData.admin,
                    provisi: this.formData.provisi,
                    erc_rate: this.formData.erc_rate,
                    penalty_rate: this.formData.penalty_rate,
                    penalty_grace_days: this.formData.penalty_grace_days,
                    eligibility_criteria: this.formData.eligibility_criteria,
                    benefits: this.formData.benefits,
                    description: this.formData.description
                };

                console.log('🎯 Updating loan product data:', submitData);

                const response = await putMithril(`/api/loan-products?id=${this.productId}`, submitData, {
                    component: this,
                    showLoading: true
                });

                if (response.success) {
                    showToast("Produk pinjaman berhasil diperbarui", "success");
                    await this.onRefresh(); // Wait for refresh to complete
                    this.onHide();
                } else {
                    showToast("Gagal memperbarui produk pinjaman: " + (response.error || "Kesalahan tidak diketahui"), "error");
                }
            } catch (error) {
                console.error("Error updating loan product:", error);

                // Check if it's a 403 Forbidden error (superuser trying to access)
                if (error.code === 403) {
                    // Redirect to dashboard
                    m.route.set('/dashboard');
                    return;
                }

                showToast("Gagal memperbarui produk pinjaman: " + error.message, "error");
            } finally {
                this.isSubmitting = false;
                m.redraw();
            }
        }
    },

    view: function (vnode) {
        const { product, onHide } = vnode.attrs;
        var subunit = getTerm('subunit');

        // Form input class constant
        const inputClassBase = "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white";
        const formInputClass = inputClassBase + " flex-1";
        const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300";
        const errorClass = "mt-1 text-sm text-red-600 dark:text-red-400";

        const subunits = vnode.attrs.subunits || [
            { id: 1, nama: 'Test Subunit 1' },
            { id: 2, nama: 'Test Subunit 2' }
        ];

        return m("div", { class: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" }, [
            m("div", {
                class: "bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto"
            }, [
                m("div", { class: "bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-lg" }, [
                    m("div", { class: "flex items-center" }, [
                        m("i", { class: "ri-money-dollar-circle-line text-orange-500 text-xl mr-2" }),
                        m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white" }, product ? "Edit Produk Pinjaman" : "Tambah Produk Pinjaman")
                    ]),
                    m("p", { class: "mt-1 text-sm text-gray-600 dark:text-gray-400" }, product ? "Perbarui informasi produk pinjaman" : "Masukkan informasi produk pinjaman baru")
                ]),

                m("form", { onsubmit: (e) => { e.preventDefault(); this.submitForm(); } }, [
                    m("div", { class: "px-6 py-4" }, [
                        m("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-6" }, [
                            // Left column
                            m("div", [
                                // Code
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "code"
                                    }, "Kode Produk"),
                                    m("input", {
                                        type: "text",
                                        id: "code",
                                        class: inputClassBase,
                                        placeholder: "Contoh: KPR001",
                                        value: this.formData.code,
                                        oninput: e => this.handleInput('code', e.target.value)
                                    }),
                                    this.errors.code && m("p", { class: "errorClass" }, this.errors.code)
                                ]),

                                // Name
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "name"
                                    }, "Nama Produk"),
                                    m("input", {
                                        type: "text",
                                        id: "name",
                                        class: inputClassBase,
                                        placeholder: "Nama produk pinjaman",
                                        value: this.formData.name,
                                        oninput: e => this.handleInput('name', e.target.value)
                                    }),
                                    this.errors.name && m("p", { class: "errorClass" }, this.errors.name)
                                ]),

                                // Subunit
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "subunit"
                                    }, getTerm('tableHeaders.subunit')),
                                    m("select", {
                                        id: "subunit",
                                        class: inputClassBase,
                                        value: this.formData.subunit_id,
                                        oninput: e => this.handleInput('subunit_id', e.target.value)
                                    }, [
                                        m("option", { value: "" }, "Pilih subunit"),
                                        subunits.map(subunit =>
                                            m("option", {
                                                value: subunit.id,
                                                selected: this.formData.subunit_id == subunit.id
                                            }, subunit.nama)
                                        )
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Pilih subunit yang akan menggunakan produk ini")
                                ]),

                                // Tenor
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "tenor"
                                    }, "Tenor"),
                                    m("div", { class: "flex gap-2" }, [
                                        m("input", {
                                            type: "number",
                                            id: "tenor",
                                            step: "1",
                                            class: formInputClass,
                                            placeholder: "Jumlah",
                                            value: this.formData.tenor,
                                            oninput: e => this.handleInput('tenor', e.target.value)
                                        }),
                                        m("select", {
                                            class: formInputClass,
                                            value: this.formData.tenor_unit,
                                            oninput: e => this.handleInput('tenor_unit', e.target.value)
                                        }, [
                                            m("option", { value: "days" }, "Hari"),
                                            m("option", { value: "weeks" }, "Minggu"),
                                            m("option", { value: "months" }, "Bulan"),
                                            m("option", { value: "years" }, "Tahun")
                                        ])
                                    ]),
                                    this.errors.tenor && m("p", { class: "errorClass" }, this.errors.tenor)
                                ]),

                                // Bunga
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "bunga"
                                    }, `${getTerm('fields.interest', 'Bunga')} (%)`),
                                    m("div", { class: "flex gap-2" }, [
                                        m("input", {
                                            type: "number",
                                            id: "bunga",
                                            step: "0.01",
                                            class: formInputClass,
                                            placeholder: "Persentase",
                                            value: this.formData.bunga,
                                            oninput: e => this.handleInput('bunga', e.target.value)
                                        }),
                                        m("select", {
                                            class: formInputClass,
                                            value: this.formData.interest_frequency,
                                            oninput: e => this.handleInput('interest_frequency', e.target.value)
                                        }, [
                                            m("option", { value: "daily" }, "Harian"),
                                            m("option", { value: "weekly" }, "Mingguan"),
                                            m("option", { value: "monthly" }, "Bulanan"),
                                            m("option", { value: "quarterly" }, "Triwulanan"),
                                            m("option", { value: "annually" }, "Tahunan"),
                                            m("option", { value: "seasonal" }, "Musiman")
                                        ])
                                    ]),
                                    this.errors.bunga && m("p", { class: "errorClass" }, this.errors.bunga)
                                ]),
                            ]),

                            // Right column
                            m("div", [
                                // Bawah (Plafon bawah)
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "bawah"
                                    }, "Plafon Bawah (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "bawah",
                                        class: inputClassBase,
                                        placeholder: "Masukkan plafon bawah",
                                        value: formatNumberInt(this.formData.bawah),
                                        oninput: e => {
                                            const rawValue = e.target.value.replace(/[^\d]/g, '');
                                            this.handleInput('bawah', rawValue);
                                        }
                                    }),
                                    this.errors.bawah && m("p", { class: "errorClass" }, this.errors.bawah)
                                ]),

                                // Atas (Plafon atas)
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "atas"
                                    }, "Plafon Atas (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "atas",
                                        class: inputClassBase,
                                        placeholder: "Masukkan plafon atas",
                                        value: formatNumberInt(this.formData.atas),
                                        oninput: e => {
                                            const rawValue = e.target.value.replace(/[^\d]/g, '');
                                            this.handleInput('atas', rawValue);
                                        }
                                    }),
                                    this.errors.atas && m("p", { class: "errorClass" }, this.errors.atas)
                                ]),

                                // Admin
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "admin"
                                    }, "Biaya Admin (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "admin",
                                        class: inputClassBase,
                                        placeholder: "Masukkan biaya admin",
                                        value: formatNumberInt(this.formData.admin),
                                        oninput: e => {
                                            const rawValue = e.target.value.replace(/[^\d]/g, '');
                                            this.handleInput('admin', rawValue);
                                        }
                                    }),
                                    this.errors.admin && m("p", { class: "errorClass" }, this.errors.admin)
                                ]),

                                // Provisi
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "provisi"
                                    }, "Biaya Provisi (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "provisi",
                                        class: inputClassBase,
                                        placeholder: "Masukkan biaya provisi",
                                        value: formatNumberInt(this.formData.provisi),
                                        oninput: e => {
                                            const rawValue = e.target.value.replace(/[^\d]/g, '');
                                            this.handleInput('provisi', rawValue);
                                        }
                                    }),
                                    this.errors.provisi && m("p", { class: "errorClass" }, this.errors.provisi)
                                ]),

                                // ERC Rate
                                m("div", { class: "mt-4" }, [
                                    m("label", {
                                        class: labelClass,
                                        for: "erc_rate"
                                    }, "Biaya Pelunasan Dini (%)"),
                                    m("input", {
                                        type: "number",
                                        id: "erc_rate",
                                        step: "0.01",
                                        min: "0",
                                        max: "100",
                                        class: inputClassBase,
                                        placeholder: "Masukkan biaya pelunasan dini dalam persen",
                                        value: this.formData.erc_rate || 0,
                                        oninput: e => this.handleInput('erc_rate', e.target.value)
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Persentase biaya tambahan jika pinjaman dilunasi sebelum waktunya")
                                ]),

                                // Penalty Section Header
                                m("div", { class: "mt-6 mb-2" }, [
                                    m("h4", { class: "text-sm font-semibold text-gray-700 dark:text-gray-300" }, "Denda Keterlambatan")
                                ]),

                                // Penalty Rate and Grace Days in a row
                                m("div", { class: "flex gap-4" }, [
                                    m("div", { class: "flex-1" }, [
                                        m("label", {
                                            class: labelClass,
                                            for: "penalty_rate"
                                        }, "Denda (% per Hari)"),
                                        m("input", {
                                            type: "number",
                                            id: "penalty_rate",
                                            step: "0.01",
                                            min: "0",
                                            max: "100",
                                            class: inputClassBase,
                                            placeholder: "0.00",
                                            value: this.formData.penalty_rate || 0,
                                            oninput: e => this.handleInput('penalty_rate', e.target.value)
                                        })
                                    ]),
                                    m("div", { class: "flex-1" }, [
                                        m("label", {
                                            class: labelClass,
                                            for: "penalty_grace_days"
                                        }, "Hari Tenggang"),
                                        m("div", { class: "flex items-center gap-2" }, [
                                            m("input", {
                                                type: "number",
                                                id: "penalty_grace_days",
                                                step: "1",
                                                min: "0",
                                                class: inputClassBase,
                                                placeholder: "0",
                                                value: this.formData.penalty_grace_days || 0,
                                                oninput: e => this.handleInput('penalty_grace_days', e.target.value)
                                            }),
                                            m("span", { class: "text-sm text-gray-500 dark:text-gray-400" }, "hari")
                                        ])
                                    ])
                                ]),

                                // Helper text for penalty
                                m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Denda harian dari jumlah angsuran. Hari tenggang adalah masa sebelum denda dikenakan setelah jatuh tempo."),
                            ])
                        ]),

                        // Additional information section
                        m("div", { class: "mt-6 space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                            m("div", { class: "flex items-center" }, [
                                m("i", { class: "ri-file-text-line text-orange-500 mr-2" }),
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Informasi Tambahan")
                            ]),
                            m("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-6 mt-2" }, [
                                // Eligibility criteria
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "eligibility"
                                    }, "Kriteria Eligibilitas"),
                                    m("textarea", {
                                        id: "eligibility",
                                        rows: "3",
                                        class: inputClassBase,
                                        placeholder: "Masukkan kriteria eligibilitas untuk produk ini",
                                        value: this.formData.eligibility_criteria || '',
                                        oninput: e => this.handleInput('eligibility_criteria', e.target.value)
                                    })
                                ]),

                                // Benefits
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "benefits"
                                    }, "Manfaat"),
                                    m("textarea", {
                                        id: "benefits",
                                        rows: "3",
                                        class: inputClassBase,
                                        placeholder: "Masukkan manfaat dari produk ini",
                                        value: this.formData.benefits || '',
                                        oninput: e => this.handleInput('benefits', e.target.value)
                                    })
                                ])
                            ]),

                            // Description
                            m("div", { class: "mt-4" }, [
                                m("label", {
                                    class: labelClass,
                                    for: "description"
                                }, "Deskripsi"),
                                m("textarea", {
                                    id: "description",
                                    rows: "3",
                                    class: inputClassBase,
                                    placeholder: "Deskripsikan produk pinjaman secara detail",
                                    value: this.formData.description || '',
                                    oninput: e => this.handleInput('description', e.target.value)
                                })
                            ])
                        ])
                    ]),

                    m("div", { class: "bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-lg flex justify-end space-x-3" }, [
                        m("button", {
                            type: "button",
                            class: "inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500",
                            onclick: onHide,
                            disabled: this.isSubmitting
                        }, [
                            m("i", { class: "ri-close-line mr-1" }),
                            "Batal"
                        ]),
                        m("button", {
                            type: "submit",
                            class: "inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500",
                            disabled: this.isSubmitting
                        }, [
                            m("i", { class: this.isSubmitting ? "ri-loader-4-line animate-spin mr-1" : "ri-save-line mr-1" }),
                            this.isSubmitting ? "Menyimpan..." : "Simpan"
                        ])
                    ])
                ])
            ])
        ]);
    }
};

export default PinjamanTab;